import { useQueryClient } from "@tanstack/react-query";
import FeeForm from "features/fees/form/FeeForm";
import { getFeeInformationListQueryKey } from "gen/endpoints/fee-information/fee-information";
import { useFeeCreate } from "gen/endpoints/fees/fees";
import { FeeCreateAPIDataRequest } from "gen/model/feeCreateAPIDataRequest";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const FundFamilySetupFundFeesAddPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["fees"]);
  const queryClient = useQueryClient();

  const { fundFamilyId } = useRequiredStringParams();

  const { mutateAsync: createFee } = useFeeCreate();

  const feesPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .setup({})
    .fund({})
    .fees({}).$;

  const defaultValues = useMemo(
    () =>
      ({
        fee: {
          name: "",
          cadence: "",
          fee_type: "",
          fee_basis_determination: "",
          fund_family_id: fundFamilyId,
          call_transaction_code_id: "",
        },
        fund_ids: [],
        applies_to_all_funds: true,
        fee_eligible_for_true_up: true,
        fee_calculations: [
          {
            fee_parts: [
              {
                fee_basis_id: "",
                fee_rate: "",
              },
            ],
          },
        ],
      }) as unknown as FeeCreateAPIDataRequest,
    [fundFamilyId],
  );

  const onCancel = useCallback(() => {
    navigate(feesPath);
  }, [feesPath, navigate]);

  return (
    <PageContent contentWidth="boxed">
      <PageHeader title={t("fees:add.title")} />
      <PageBody>
        <FeeForm
          feeData={defaultValues}
          fundFamilyId={fundFamilyId}
          submitButtonText={t("fees:add.save")}
          successMessage={t("fees:add.success")}
          onSubmit={async (data: FeeCreateAPIDataRequest) => {
            await createFee({ fundFamilyId, data });
          }}
          onSuccess={async () => {
            await queryClient.invalidateQueries({
              queryKey: getFeeInformationListQueryKey(fundFamilyId),
            });
            navigate(feesPath);
          }}
          onCancel={onCancel}
        />
      </PageBody>
    </PageContent>
  );
};
